if (!customElements.get('product-description-faq')) {
  class ProductDescriptionFaq extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.sourceElement = this.querySelector('[data-product-description-source]');
      this.tabButtons = Array.from(this.querySelectorAll('[data-product-info-tab]'));
      this.panels = Array.from(this.querySelectorAll('[data-product-info-panel]'));
      this.faqList = this.querySelector('[data-product-faq-list]');
      this.faqEmpty = this.querySelector('[data-faq-empty]');
      this.descriptionEmpty = this.querySelector('[data-description-empty]');

      this.boundClick = this.handleClick.bind(this);
      this.boundKeydown = this.handleKeydown.bind(this);
      this.addEventListener('click', this.boundClick);
      this.addEventListener('keydown', this.boundKeydown);

      this.buildFromProductDescription();
      this.activateInitialTab();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      this.removeEventListener('keydown', this.boundKeydown);
    }

    buildFromProductDescription() {
      if (!this.sourceElement) return;

      const originalHtml = this.sourceElement.innerHTML.trim();
      const parsed = this.parseProductDescription(originalHtml);
      const descriptionPanelSource = this.sourceElement.closest('[data-product-info-panel="description"]')
        ? this.sourceElement
        : null;

      if (descriptionPanelSource) {
        descriptionPanelSource.innerHTML = parsed.descriptionHtml;
        const isEmpty = !this.hasMeaningfulContent(descriptionPanelSource);
        if (this.descriptionEmpty) this.descriptionEmpty.hidden = !isEmpty;
      }

      this.renderFaqs(parsed.faqs);
    }

    parseProductDescription(html) {
      if (!html) return { descriptionHtml: '', faqs: [] };

      const parser = new DOMParser();
      const documentFragment = parser.parseFromString(html, 'text/html');
      const body = documentFragment.body;
      const faqs = [];
      const seenQuestions = new Set();
      const keywords = this.faqKeywords;

      body.querySelectorAll('script').forEach((script) => script.remove());

      const addFaq = (question, answerHtml) => {
        const cleanQuestion = this.cleanQuestion(question);
        const cleanAnswer = this.cleanAnswerHtml(answerHtml);
        const key = this.normalizeText(cleanQuestion);
        if (!cleanQuestion || !cleanAnswer || seenQuestions.has(key)) return;
        seenQuestions.add(key);
        faqs.push({ question: cleanQuestion, answerHtml: cleanAnswer });
      };

      const marker = Array.from(body.querySelectorAll('h1, h2, h3, h4, h5, h6')).find((heading) => {
        const text = this.normalizeText(heading.textContent);
        return keywords.some((keyword) => text === keyword || text.includes('frequently asked question'));
      });

      if (marker) {
        const faqRoot = documentFragment.createElement('div');
        let sibling = marker.nextSibling;
        while (sibling) {
          const next = sibling.nextSibling;
          faqRoot.appendChild(sibling);
          sibling = next;
        }
        marker.remove();
        this.extractFaqContent(faqRoot, addFaq, true);
      } else {
        const dedicatedContainers = Array.from(body.querySelectorAll('[data-faq-section], .faq-section, .product-faq, #faq, #faqs'));
        dedicatedContainers.forEach((container) => {
          this.extractFaqContent(container, addFaq, true);
          container.remove();
        });
        this.extractFaqContent(body, addFaq, false);
      }

      this.removeEmptyContainers(body);
      const descriptionHtml = body.innerHTML.trim();
      return { descriptionHtml, faqs };
    }

    extractFaqContent(root, addFaq, relaxedHeadings) {
      this.extractDetails(root, addFaq);
      this.extractDefinitionLists(root, addFaq);
      this.extractKnownFaqItems(root, addFaq);
      this.extractQuestionSequences(root, addFaq, relaxedHeadings);
    }

    extractDetails(root, addFaq) {
      Array.from(root.querySelectorAll('details')).forEach((details) => {
        const summary = details.querySelector(':scope > summary');
        if (!summary) return;
        const answer = details.cloneNode(true);
        answer.querySelector(':scope > summary')?.remove();
        addFaq(summary.textContent, answer.innerHTML);
        details.remove();
      });
    }

    extractDefinitionLists(root, addFaq) {
      Array.from(root.querySelectorAll('dl')).forEach((list) => {
        const terms = Array.from(list.querySelectorAll(':scope > dt'));
        if (!terms.length) return;

        terms.forEach((term) => {
          const answers = [];
          let sibling = term.nextElementSibling;
          while (sibling && sibling.tagName === 'DD') {
            answers.push(sibling.innerHTML);
            sibling = sibling.nextElementSibling;
          }
          addFaq(term.textContent, answers.join(''));
        });
        list.remove();
      });
    }

    extractKnownFaqItems(root, addFaq) {
      const itemSelector = '[data-faq-item], .faq-item, .product-faq__item';
      Array.from(root.querySelectorAll(itemSelector)).forEach((item) => {
        if (item.matches('details') || item.closest('details')) return;
        const question = item.querySelector('[data-faq-question], .faq-question, h2, h3, h4, h5, h6, strong');
        const answer = item.querySelector('[data-faq-answer], .faq-answer, .answer');
        if (!question || !answer) return;
        addFaq(question.textContent, answer.innerHTML);
        item.remove();
      });
    }

    extractQuestionSequences(root, addFaq, relaxedHeadings) {
      const processContainer = (container) => {
        const children = Array.from(container.children).filter((child) => !child.matches('script, style'));
        const candidates = children.filter((child) => this.isQuestionElement(child, relaxedHeadings));

        if (candidates.length) {
          candidates.forEach((questionElement, index) => {
            const nextQuestion = candidates[index + 1] || null;
            const answerNodes = [];
            let sibling = questionElement.nextElementSibling;
            while (sibling && sibling !== nextQuestion) {
              answerNodes.push(sibling);
              sibling = sibling.nextElementSibling;
            }

            const extracted = this.questionAndInlineAnswer(questionElement);
            const answerParts = [];
            if (extracted.inlineAnswer) answerParts.push(extracted.inlineAnswer);
            answerNodes.forEach((node) => answerParts.push(node.outerHTML));
            addFaq(extracted.question, answerParts.join(''));

            questionElement.remove();
            answerNodes.forEach((node) => node.remove());
          });
          return;
        }

        children.forEach((child) => {
          if (child.children.length && !child.matches('table, ul, ol')) processContainer(child);
        });
      };

      processContainer(root);
    }

    isQuestionElement(element, relaxedHeadings) {
      if (element.matches('h2, h3, h4, h5, h6')) {
        return relaxedHeadings || /\?\s*$/.test(element.textContent.trim());
      }

      if (element.tagName !== 'P') return false;
      const firstMeaningful = Array.from(element.childNodes).find((node) => {
        return node.nodeType === Node.ELEMENT_NODE || (node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      });
      const strongFirst = firstMeaningful?.nodeType === Node.ELEMENT_NODE
        && ['STRONG', 'B'].includes(firstMeaningful.tagName);
      if (!strongFirst) return false;

      const strongText = firstMeaningful.textContent.trim();
      return relaxedHeadings || /\?\s*$/.test(strongText);
    }

    questionAndInlineAnswer(element) {
      if (!element.matches('p')) return { question: element.textContent, inlineAnswer: '' };

      const clone = element.cloneNode(true);
      const strong = clone.querySelector(':scope > strong:first-child, :scope > b:first-child');
      const question = strong?.textContent || element.textContent;
      strong?.remove();
      while (clone.firstChild && (
        clone.firstChild.nodeName === 'BR'
        || (clone.firstChild.nodeType === Node.TEXT_NODE && !clone.firstChild.textContent.trim())
      )) {
        clone.firstChild.remove();
      }
      return { question, inlineAnswer: clone.innerHTML.trim() };
    }

    cleanQuestion(value) {
      return String(value || '')
        .replace(/^\s*(?:q(?:uestion)?\s*)?\d+\s*[.)\-:]\s*/i, '')
        .replace(/^\s*[-–—]\s*/, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    cleanAnswerHtml(value) {
      const parser = new DOMParser();
      const documentFragment = parser.parseFromString(String(value || ''), 'text/html');
      documentFragment.body.querySelectorAll('script').forEach((script) => script.remove());
      this.removeEmptyContainers(documentFragment.body);
      const html = documentFragment.body.innerHTML.trim();
      const text = documentFragment.body.textContent.replace(/\s+/g, ' ').trim();
      return text || documentFragment.body.querySelector('img, video, iframe, table, ul, ol') ? html : '';
    }

    removeEmptyContainers(root) {
      const candidates = Array.from(root.querySelectorAll('p, div, section')).reverse();
      candidates.forEach((element) => {
        const text = element.textContent.replace(/\s+/g, '').trim();
        const media = element.querySelector('img, video, iframe, table, ul, ol, a[href]');
        if (!text && !media) element.remove();
      });
    }

    renderFaqs(faqs) {
      if (!this.faqList) return;
      this.faqList.replaceChildren();

      faqs.forEach((faq, index) => {
        const item = document.createElement('article');
        const button = document.createElement('button');
        const number = document.createElement('span');
        const question = document.createElement('span');
        const toggle = document.createElement('span');
        const answerWrap = document.createElement('div');
        const answerClip = document.createElement('div');
        const answer = document.createElement('div');
        const answerId = `ProductFaqAnswer-${this.dataset.sectionId}-${index + 1}`;

        item.className = 'product-description-faq__item';
        item.dataset.productFaqItem = '';
        button.className = 'product-description-faq__faq-button';
        button.type = 'button';
        button.dataset.productFaqToggle = '';
        button.setAttribute('aria-expanded', 'false');
        button.setAttribute('aria-controls', answerId);
        number.className = 'product-description-faq__number';
        number.setAttribute('aria-hidden', 'true');
        question.className = 'product-description-faq__question';
        question.textContent = faq.question;
        toggle.className = 'product-description-faq__toggle';
        toggle.setAttribute('aria-hidden', 'true');
        answerWrap.className = 'product-description-faq__answer-wrap';
        answerWrap.id = answerId;
        answerWrap.setAttribute('aria-hidden', 'true');
        answerClip.className = 'product-description-faq__answer-clip';
        answer.className = 'product-description-faq__answer rte';
        answer.innerHTML = faq.answerHtml;

        button.append(number, question, toggle);
        answerClip.append(answer);
        answerWrap.append(answerClip);
        item.append(button, answerWrap);
        this.faqList.append(item);
      });

      const count = this.querySelector('[data-faq-count]');
      if (count) {
        count.textContent = String(faqs.length);
        count.hidden = faqs.length === 0;
      }

      if (this.faqEmpty) this.faqEmpty.hidden = faqs.length > 0;

      if (faqs.length && this.dataset.openFirst === 'true') {
        const firstItem = this.faqList.querySelector('[data-product-faq-item]');
        if (firstItem) this.setFaqState(firstItem, true);
      }

      if (!faqs.length && this.dataset.hideEmptyFaq === 'true') this.hideEmptyFaqTab();
    }

    hideEmptyFaqTab() {
      const faqTab = this.querySelector('[data-product-info-tab="faq"]');
      const faqPanel = this.querySelector('[data-product-info-panel="faq"]');
      if (faqTab) faqTab.hidden = true;
      if (faqPanel) faqPanel.hidden = true;
    }

    activateInitialTab() {
      const requested = this.dataset.defaultTab || 'description';
      const requestedButton = this.querySelector(`[data-product-info-tab="${requested}"]:not([hidden])`);
      const fallbackButton = this.tabButtons.find((button) => !button.hidden);
      const activeButton = requestedButton || fallbackButton;
      if (activeButton) this.activateTab(activeButton.dataset.productInfoTab, false);
    }

    handleClick(event) {
      const tab = event.target.closest('[data-product-info-tab]');
      if (tab && this.contains(tab)) {
        this.activateTab(tab.dataset.productInfoTab, false);
        return;
      }

      const faqToggle = event.target.closest('[data-product-faq-toggle]');
      if (!faqToggle || !this.contains(faqToggle)) return;
      const item = faqToggle.closest('[data-product-faq-item]');
      if (!item) return;
      this.setFaqState(item, !item.classList.contains('is-open'));
    }

    handleKeydown(event) {
      const currentTab = event.target.closest('[data-product-info-tab]');
      if (!currentTab) return;

      const visibleTabs = this.tabButtons.filter((button) => !button.hidden);
      const currentIndex = visibleTabs.indexOf(currentTab);
      if (currentIndex < 0) return;

      let nextIndex = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % visibleTabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = visibleTabs.length - 1;
      if (nextIndex === null) return;

      event.preventDefault();
      const nextTab = visibleTabs[nextIndex];
      this.activateTab(nextTab.dataset.productInfoTab, true);
    }

    activateTab(tabName, focusTab) {
      this.tabButtons.forEach((button) => {
        const active = button.dataset.productInfoTab === tabName && !button.hidden;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        if (active && focusTab) button.focus();
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel.dataset.productInfoPanel !== tabName;
      });
    }

    setFaqState(item, open) {
      if (open && this.dataset.allowMultiple !== 'true') {
        this.querySelectorAll('[data-product-faq-item].is-open').forEach((otherItem) => {
          if (otherItem !== item) this.setFaqState(otherItem, false);
        });
      }

      item.classList.toggle('is-open', open);
      const button = item.querySelector('[data-product-faq-toggle]');
      const answer = item.querySelector('.product-description-faq__answer-wrap');
      button?.setAttribute('aria-expanded', String(open));
      answer?.setAttribute('aria-hidden', String(!open));
    }

    hasMeaningfulContent(element) {
      const text = element.textContent.replace(/\s+/g, '').trim();
      return Boolean(text || element.querySelector('img, video, iframe, table, ul, ol'));
    }

    normalizeText(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    get faqKeywords() {
      const configured = String(this.dataset.faqKeywords || 'FAQ, FAQs, Frequently Asked Questions')
        .split(',')
        .map((keyword) => this.normalizeText(keyword))
        .filter(Boolean);
      return Array.from(new Set([...configured, 'faq', 'faqs', 'frequently asked questions']));
    }
  }

  customElements.define('product-description-faq', ProductDescriptionFaq);
}
